import { min, max } from 'lib/array/arrayUtils'
import moment from 'moment'

export type OrderFlightDetailsState = App.ApiStateRecord<{ flight?: App.FlightOrderItemDetails }>

function bedbankDateToString(val: unknown): string | undefined {
  if (val == null) return undefined
  if (typeof val === 'string') return val
  if (moment.isMoment(val)) return val.format('YYYY-MM-DD')
  return undefined
}

function parseOrderDepartureCandidate(value: string | Date | undefined | null): Date | undefined {
  if (value == null || value === '') return undefined
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? undefined : d
}

function collectDepartureCandidates(order: App.Order, include: (status: string) => boolean): Array<Date> {
  const candidates: Array<Date> = []
  for (const item of order.items) {
    if (!include(item.status)) continue
    const d = parseOrderDepartureCandidate(item.reservation?.startDate)
    if (d) candidates.push(d)
  }
  for (const item of order.bedbankItems) {
    if (!include(item.status)) continue
    const s = bedbankDateToString(item.checkIn)
    const d = s ? parseOrderDepartureCandidate(s) : undefined
    if (d) candidates.push(d)
  }
  for (const item of order.tourItems) {
    if (!include(item.status)) continue
    const d = parseOrderDepartureCandidate(item.tour?.startDate)
    if (d) candidates.push(d)
  }
  for (const item of order.flightItems) {
    if (!include(item.status)) continue
    const d = parseOrderDepartureCandidate(item.departureDate)
    if (d) candidates.push(d)
  }
  for (const item of order.cruiseItems) {
    if (!include(item.status)) continue
    const d = parseOrderDepartureCandidate(item.departureDate)
    if (d) candidates.push(d)
  }
  return candidates
}

function collectEndCandidates(order: App.Order, include: (status: string) => boolean): Array<Date> {
  const candidates: Array<Date> = []
  for (const item of order.items) {
    if (!include(item.status)) continue
    const d = parseOrderDepartureCandidate(item.reservation?.endDate)
    if (d) candidates.push(d)
  }
  for (const item of order.bedbankItems) {
    if (!include(item.status)) continue
    const s = bedbankDateToString(item.checkOut)
    const d = s ? parseOrderDepartureCandidate(s) : undefined
    if (d) candidates.push(d)
  }
  for (const item of order.tourItems) {
    if (!include(item.status)) continue
    const d = parseOrderDepartureCandidate(item.tour?.endDate)
    if (d) candidates.push(d)
  }
  for (const item of order.flightItems) {
    if (!include(item.status)) continue
    const d = parseOrderDepartureCandidate(item.arrivalDate)
    if (d) candidates.push(d)
  }
  for (const item of order.cruiseItems) {
    if (!include(item.status)) continue
    const d = parseOrderDepartureCandidate(item.arrivalDate)
    if (d) candidates.push(d)
  }
  return candidates
}

const isNotCancelled = (status: string) => status !== 'cancelled'

export function getOrderDisplayDepartureDate(order: App.Order): Date | undefined {
  return min(collectDepartureCandidates(order, isNotCancelled), d => d)
}

export function getOrderDisplayEndDate(order: App.Order): Date | undefined {
  return max(collectEndCandidates(order, isNotCancelled), d => d)
}

/** All bookable line items — used for post-checkout settlement checks in the eSIM promo hook. */
export function getAllOrderLineItems(order: App.Order) {
  return [...order.items, ...order.bedbankItems, ...order.flightItems, ...(order.cruiseItems ?? []), ...(order.tourItems ?? [])]
}

export function hasUnsettledOrderLineItems(order: App.Order): boolean {
  return getAllOrderLineItems(order).some(item => item.status !== 'completed' && item.status !== 'cancelled')
}

export function isOrderLineItemsSettled(order: App.Order): boolean {
  return getAllOrderLineItems(order).every(item => item.status === 'completed' || item.status === 'cancelled')
}
